import React, { useState } from 'react';
import styled from '@emotion/styled';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';

import * as actions from '~actions';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import Field from '~ComponentsImproved/RHF/Field';
import Heading from '~ComponentsImproved/Typography/Heading';
import Loader from '~ComponentsImproved/Loader';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useDispatch, useI18n, useLogger } from '~Hooks';
import { BlackButton, BlueButton, Box, Input } from '~Styles';
import { ModalContent } from '~Styles/modal';
import { EMAIL_PATTERN } from '~Utils/email';
import { CURRENCY, ReportsServiceCategoryInput } from '~../__queries__';
import { SendZReport, SendZReportVariables } from './__queries__';

const SEND_Z_REPORT_EMAIL = gql`
  mutation SendZReport(
    $sendZReportVenueId: String!
    $sendZReportEmail: String!
    $sendZReportDay: DateTime!
    $orgNumber: String
    $categories: [ReportsServiceCategoryInput!]
    $timezone: String
    $language: String
    $currency: CURRENCY
  ) {
    sendZReport(
      venueId: $sendZReportVenueId
      email: $sendZReportEmail
      day: $sendZReportDay
      orgNumber: $orgNumber
      categories: $categories
      timezone: $timezone
      language: $language
      currency: $currency
    ) {
      error
      message
      success
    }
  }
`;

const Wrapper = styled(ModalContent)`
  margin: 0 0 25% 0;
  max-width: 400px;
`;

type FormInputs = {
  email: string;
};

interface SendZReportEmailProps {
  venueId: string;
  reportDay: string;
  orgNumber: string;
  categories: ReportsServiceCategoryInput[];
  timezone: string;
  language: string;
  currency: CURRENCY;
  disabledReason?: string; // helper text for why button is disabled
}

export const SendZReportEmail: React.FC<SendZReportEmailProps> = ({
  venueId,
  reportDay,
  orgNumber,
  categories,
  timezone,
  language,
  currency,
  disabledReason,
}) => {
  const { i18n } = useI18n();
  const logger = useLogger('send-z-report-email');
  const dispatch = useDispatch();
  const modal = useModal();

  const formMethods = useForm<FormInputs>();
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = formMethods;

  const [hasError, setHasError] = useState<boolean>(false);

  const [sendReportEmail, { loading: sending }] = useMutation<SendZReport, SendZReportVariables>(
    SEND_Z_REPORT_EMAIL,
  );

  const closeModal = () => {
    modal.hide();
    formMethods.reset();
    setHasError(false);
  };

  const onSubmit: SubmitHandler<FormInputs> = async ({ email }) => {
    const recipient = email.trim();
    setHasError(false);

    if (!reportDay) {
      logger.warn('Attempted to send Z-report email without a report day', { venueId });
      setHasError(true);
      return;
    }

    logger.info('Sending Z-report email', { venueId, reportDay, timezone, language, currency });

    try {
      const result = await sendReportEmail({
        variables: {
          sendZReportVenueId: venueId,
          sendZReportEmail: recipient,
          sendZReportDay: reportDay,
          orgNumber,
          categories,
          timezone,
          language,
          currency,
        },
      });

      if (!result.data?.sendZReport.success) {
        throw new Error(result.data?.sendZReport.error || 'Unknown error');
      }

      logger.info('Z-report email sent', { venueId, reportDay });
      dispatch(actions.addToast(i18n('Report.ZReportEmailSent', recipient), true));
      closeModal();
    } catch (error) {
      logger.error('Failed to send Z-report email', {
        venueId,
        reportDay,
        reason: error instanceof Error ? error.message : String(error),
      });
      setHasError(true);
    }
  };

  const openModalButton = useButton({
    onClick: modal.toggle,
    disabled: !!disabledReason,
  });

  return (
    <>
      <Box marginTop="medium">
        <BlackButton fluid {...openModalButton}>
          {i18n('Report.EmailReport')}
        </BlackButton>
        {disabledReason && (
          <Box marginTop="xs">
            <Paragraph level={4} color="var(--colorBlueGray200)" style={{ textAlign: 'center' }}>
              {disabledReason}
            </Paragraph>
          </Box>
        )}
      </Box>
      <Modal isShowing={modal.isShowing} hide={closeModal}>
        <Wrapper>
          <Heading level={3} color="var(--colorWhite)" bottom={16}>
            {i18n('Report.EmailZReportTitle')}
          </Heading>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Field name="email" marginBottom="small">
                <Input
                  fluid
                  dark
                  type="email"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  placeholder="example@email.com"
                  {...register('email', {
                    required: { value: true, message: i18n('GC.Create.Email.Required') },
                    pattern: {
                      value: EMAIL_PATTERN,
                      message: i18n('GC.Create.Email.Invalid'),
                    },
                  })}
                  error={!!errors.email}
                />
              </Field>
              {hasError && (
                <Box marginBottom="small">
                  <Paragraph level={4} color="var(--colorUIRed)">
                    {i18n('Report.ZReportEmailError')}
                  </Paragraph>
                </Box>
              )}
              <BlueButton fluid type="submit" disabled={sending}>
                {sending ? <Loader size="sm" /> : i18n('General.Send')}
              </BlueButton>
            </form>
          </FormProvider>
        </Wrapper>
      </Modal>
    </>
  );
};
